import axios from "axios";
import { useContext, useRef, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import { affiliateError } from "../../hooks/api/admin/useAffiliates";
import { ADMIN_API_URL } from "../../utils/env";
import { formatAEVO } from "./budget";
import { makerEnvironmentMatches } from "./environment";

interface Preparation {
  preparation_id?: string;
  environment: string;
  testnet_only: boolean;
  week_id: string;
  launch_epoch: number;
  status: "preparing" | "ready" | "failed" | "committed";
  pool_units?: string;
  maker_paid_units?: string;
  trading_total_units?: string;
  error?: string;
}

export function verifyMakerPreparation(
  data: Preparation,
  week: string,
  epoch: number
) {
  if (
    !makerEnvironmentMatches(data) ||
    data.week_id !== week ||
    data.launch_epoch !== epoch ||
    !["preparing", "ready", "failed", "committed"].includes(data.status)
  )
    throw new Error(
      "Weekly preparation does not match this network, week and launch epoch."
    );
  if (
    ["ready", "committed"].includes(data.status) &&
    [data.pool_units, data.maker_paid_units, data.trading_total_units].some(
      (value) => typeof value !== "string" || !/^\d+$/.test(value)
    )
  )
    throw new Error("Verified weekly accounting totals are missing.");
  if (data.status === "ready" && !data.preparation_id)
    throw new Error("Verified preparation identity is missing.");
  return data;
}

export function WeeklySettlement({
  ready,
  serverTime,
  onRecorded,
}: {
  ready: boolean;
  serverTime?: string;
  onRecorded: () => Promise<unknown>;
}) {
  const { queryConfig } = useContext(AuthContext);
  const [week, setWeek] = useState("");
  const [epoch, setEpoch] = useState(1);
  const [preparation, setPreparation] = useState<Preparation>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [uncertain, setUncertain] = useState(false);
  const inFlight = useRef(false);
  const clear = () => {
    setPreparation(undefined);
    setError("");
    setUncertain(false);
  };
  const validate = () => {
    const start = Date.parse(`${week}T08:00:00Z`);
    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(week) ||
      !Number.isFinite(start) ||
      new Date(start).getUTCDay() !== 1 ||
      !serverTime ||
      start + 7 * 24 * 60 * 60 * 1000 > Date.parse(serverTime)
    )
      throw new Error(
        "Choose a completed reward week beginning Monday at 08:00 UTC."
      );
  };
  const request = async (action: "prepare" | "refresh" | "commit") => {
    if (!ready || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      validate();
      const body = { week_id: week, launch_epoch: epoch };
      if (action === "commit" && preparation?.status !== "ready")
        throw new Error(
          "A current ready preparation is required before recording rewards."
        );
      if (action === "commit") setUncertain(true);
      const path =
        action === "commit"
          ? "makers/weekly-settlement/commit"
          : "makers/weekly-settlement";
      const response =
        action === "refresh"
          ? await axios.get(
              ADMIN_API_URL(`${path}?week_id=${encodeURIComponent(week)}`),
              queryConfig()
            )
          : await axios.post(ADMIN_API_URL(path), body, {
              ...queryConfig(),
              timeout: 20000,
            });
      let { data } = response;
      // A successful command receipt confirms acceptance, not the immutable
      // weekly ledger. Older servers return only this receipt on first commit.
      if (action === "commit" && !data?.status) {
        if (
          data?.success !== true ||
          !makerEnvironmentMatches(data) ||
          typeof data.request_id !== "string" ||
          !data.request_id ||
          !Number.isFinite(Date.parse(data.server_time))
        )
          throw new Error("Weekly recording receipt could not be verified.");
        const status = await axios.get(
          ADMIN_API_URL(
            `makers/weekly-settlement?week_id=${encodeURIComponent(week)}`
          ),
          queryConfig()
        );
        data = status.data;
      }
      const confirmed = verifyMakerPreparation(data, week, epoch);
      setPreparation(confirmed);
      // A refresh can still return the preparation while its accepted command
      // is being applied. Only the weekly ledger proves recording completed.
      if (confirmed.status === "committed") setUncertain(false);
      else if (action === "prepare") setUncertain(false);
      if (confirmed.status === "committed") {
        try {
          await onRecorded();
        } catch {
          setError(
            "Rewards were recorded. Refresh server state to reload the ledger."
          );
        }
      }
    } catch (failure) {
      if ((failure as any)?.response?.status === 409) {
        setPreparation(undefined);
        setUncertain(false);
        setError(
          "Preparation is stale, expired or incomplete. Prepare this week again."
        );
      } else setError(affiliateError(failure));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  return (
    <section aria-label="Weekly maker rewards">
      <h2>Weekly maker rewards</h2>
      <p>
        Prepare a completed Monday 08:00 UTC reward week from recorded quotes
        and approved maker families. Preparation does not record rewards or move
        funds.
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          request("prepare");
        }}
      >
        <label htmlFor="maker-settlement-week">
          Reward week starts (UTC)
          <input
            id="maker-settlement-week"
            type="date"
            value={week}
            disabled={busy}
            required
            onChange={(event) => {
              setWeek(event.target.value);
              clear();
            }}
          />
        </label>
        <label htmlFor="maker-settlement-epoch">
          Launch epoch
          <select
            id="maker-settlement-epoch"
            value={epoch}
            disabled={busy}
            onChange={(event) => {
              setEpoch(Number(event.target.value));
              clear();
            }}
          >
            {[1, 2, 3, 4].map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" disabled={!ready || busy}>
          Prepare weekly rewards
        </button>
        <button
          type="button"
          disabled={!ready || busy || !week}
          onClick={() => request("refresh")}
        >
          Refresh preparation
        </button>
      </form>
      {error && <p role="alert">{error}</p>}
      {uncertain && (
        <p>
          Recording is not confirmed. Refresh preparation or retry the same
          week; the backend weekly receipt prevents duplicate awards.
        </p>
      )}
      {preparation && (
        <>
          <p role="status" style={{ overflowWrap: "anywhere" }}>
            Preparation: {preparation.status}
            {preparation.preparation_id
              ? ` · ${preparation.preparation_id}`
              : ""}
          </p>
          {preparation.status === "preparing" && (
            <p>
              Quote verification is running. Refresh preparation to check its
              progress.
            </p>
          )}
          {preparation.error && <p role="alert">{preparation.error}</p>}
          {["ready", "committed"].includes(preparation.status) && (
            <dl>
              <dt>Weekly pool</dt>
              <dd>{formatAEVO(preparation.pool_units!)} AEVO</dd>
              <dt>Maker rewards</dt>
              <dd>{formatAEVO(preparation.maker_paid_units!)} AEVO</dd>
              <dt>Trading rewards</dt>
              <dd>{formatAEVO(preparation.trading_total_units!)} AEVO</dd>
            </dl>
          )}
          {preparation.status === "ready" && (
            <>
              <p>
                Review these server totals before recording. Recording is
                separate from payout and claim publication.
              </p>
              <button
                type="button"
                disabled={!ready || busy}
                onClick={() => request("commit")}
              >
                Record weekly rewards
              </button>
            </>
          )}
          {preparation.status === "committed" && (
            <p>
              Weekly accounting is recorded. This does not publish a claim root
              or transfer tokens.
            </p>
          )}
        </>
      )}
    </section>
  );
}
